import type { ComponentChildren } from 'preact';
import { useEffect, useId, useLayoutEffect, useRef } from 'preact/hooks';
import { backButton } from '../../app/backButton';
import { ru } from '../../i18n/ru';
import { PAD_START_ATTR } from '../../input/focusNav';
import './Dialog.css';

const padStart = { [PAD_START_ATTR]: '' };

export interface DialogProps {
  title: string;
  onClose(): void;
  /** Wider panel for content that can use two columns (on screens that have the room). */
  wide?: boolean;
  children: ComponentChildren;
}

/**
 * Modal window over the current screen. Closes with the ✕ button, Escape, Android's Back, a
 * gamepad's B or a tap on the dimmed backdrop, and gives focus back to where it was when it
 * closes.
 */
export function Dialog({ title, onClose, wide = false, children }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus({ preventScroll: true });
    };
  }, []);
  // Before the dialog is painted: a Back pressed right away must already close it.
  useLayoutEffect(() => backButton.register(() => onCloseRef.current()), []);

  return (
    <div class="dialog-layer">
      <button
        type="button"
        class="dialog-layer__backdrop"
        tabIndex={-1}
        aria-label={ru.common.close}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        class={`dialog panel${wide ? ' dialog--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header class="dialog__header">
          <h2 id={titleId} class="dialog__title title-text">
            {title}
          </h2>
          <button
            type="button"
            class="dialog__close"
            aria-label={ru.common.close}
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        {/* A gamepad's focus starts in the content, not on ✕ (B closes the dialog anyway). */}
        <div class="dialog__body" {...padStart}>
          {children}
        </div>
      </div>
    </div>
  );
}
